<template>
  <span class="eb-help-wrap">
    <b-button
      size="sm"
      variant="outline-secondary"
      title="How to use the Event Board"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Using the Event Board"
      size="lg"
      ok-only
      scrollable
    >
      <div class="eb-help-body">
        <h4>What this page is</h4>
        <p>
          The Event Board is the same events as the <b>Event Log</b>, arranged as four lanes
          by status. The log is for searching the record; the board is for working the
          queue: seeing what's piling up where, and moving it.
        </p>

        <hr/>

        <h4>The lanes</h4>
        <ul>
          <li><b>New</b>: filed, nobody has picked it up.</li>
          <li><b>In progress</b>: somebody is working it.</li>
          <li><b>Awaiting follow-up</b>: waiting on a callback, a part, or someone else.</li>
          <li><b>Resolved</b>: closed out.</li>
        </ul>
        <p>
          Within a lane, cards lead with the loudest and the oldest: high severity first,
          then whatever has sat the longest. An open event more than a week old is marked,
          for example <b>12d open</b>, because the card nobody has touched in a month
          matters more than this morning's.
        </p>

        <hr/>

        <h4>Moving an event</h4>
        <ul>
          <li>
            Drag a card from one lane to another to change its status. As you drag, a lane
            that can take the card lights up, one that can't fades out, and each lane says
            what letting go will do.
          </li>
          <li>
            The board only offers the moves the workflow allows, so nothing you can drop
            will be refused after the fact. Nothing returns to <b>New</b>: an event
            that has been picked up has been picked up.
          </li>
          <li>
            Reopening a resolved event (dragging it back to <b>In progress</b>) is limited
            to Tech Support and fleet admins on Coach Manager accounts, so a stray drag
            can't reopen closed work. For everyone else, resolved cards don't move.
          </li>
          <li>
            The card pulses green where a move stuck, or red if the server refused it and
            sent the card back; a blue ring marks a card someone else just filed or moved.
          </li>
          <li>
            <b>On a phone or tablet there is no dragging.</b> A touch that moves is
            indistinguishable from scrolling the lane, so the board is read-only there:
            tap a card to open the event and use its <b>Move to</b> buttons, which run the
            same workflow with the same rules.
          </li>
        </ul>

        <hr/>

        <h4>Narrowing the board</h4>
        <ul>
          <li>
            <b>Mine</b>, <b>Everyone</b>, and <b>Unassigned</b> scope the whole board by
            assignee, or pick one person from the dropdown. The board opens on
            <b>Mine</b> when you have open work, and on <b>Everyone</b> when you don't.
          </li>
          <li>
            The <b>search</b> box narrows the lanes as you type. It matches the same
            fields the Event Log search does (the description, driver, vehicle, charter,
            customer, location, action items, follow-up notes, the category, and any extra
            fields on the entry), and a bare <b>12</b> finds event #12 exactly, not #112.
          </li>
          <li>
            <b>Type</b> and <b>Severity</b> chips are independent toggles rather than a
            one-of-each choice: turn on as many as you like, and turning them all off
            means everything again, so <b>High</b> + <b>Normal</b> shows both,
            and there is no &ldquo;All&rdquo; chip to press. There is no Status group
            either: the lanes are the status.
          </li>
          <li>
            The <b>driver</b> and <b>vehicle</b> dropdowns only list values that actually
            appear in what you are looking at: narrow by Kudos and the driver list
            shrinks to the drivers with kudos. Every control stacks, so <b>Issue</b> +
            <b>High</b> + <b>Mine</b> is your own serious work across all four lanes.
          </li>
          <li>
            On a phone every control, search included, folds behind a
            <b>Filters</b> button, so the board opens straight onto the lanes. The badge
            counts what is still narrowing it, so a search you left running is never
            hidden without a trace.
          </li>
          <li>
            <b>Resolved 7d / 30d / 90d</b> sets how far back the Resolved lane reaches, by
            when each event was <i>closed</i>. It defaults to 7 days so the lane stays a
            list of recent closures rather than a growing history. The open lanes are
            never date-bounded: old open work is the whole point of the board.
          </li>
          <li>
            The board keeps itself current while it is open: cards other people file or
            move land in their lanes on their own, with a brief blue ring so you can see
            what changed. Nothing moves while you are mid-drag.
          </li>
        </ul>

        <hr/>

        <h4>What the board doesn't show</h4>
        <p>
          The lanes load the newest events rather than your entire history, and the
          Resolved lane reaches back a limited distance. When a lane holds more than it
          loaded, it says so and links to the Event Log filtered to that status, and
          that page searches the full record. Click any card to open the event itself,
          where you can comment, edit, attach photos, and print; the back arrow returns
          you here.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
// how-to modal for the ERM Board, following the coaching_portal_help /
// actuals_*_help house pattern (info-icon button + ok-only scrollable modal).
// the board carries no standing description, so this is where the drag rules
// and the Resolved window get explained.
export default {
  name: 'erm_board_help',
  data() {
    return { show_help: false }
  },
}
</script>

<style scoped>
.eb-help-body h4 { font-size: 1.05rem; margin-top: 4px; }
.eb-help-body p, .eb-help-body li { font-size: 0.95rem; }
.eb-help-body li { margin-bottom: 6px; }
</style>
